import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { loginRequest } from '../api/authApi';
import { subscribeToPush } from '../utils/pushNotifications';
import { saveSubscription } from '../api/pushApi';
import Logo from '../components/Logo';
import PasswordInput from '../components/PasswordInput';

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoginError('');
    setLoading(true);

    try {
      const result = await loginRequest(data.email, data.password, 'driver');

      if (result.success) {
        localStorage.setItem('token', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));

        // Ask for notification permission and save the subscription.
        // This runs in the background — we don't block navigation on it,
        // and we don't show an error if it fails, since declining
        // notifications shouldn't prevent using the rest of the app.
        subscribeToPush()
          .then((subscription) => {
            if (subscription) {
              saveSubscription(subscription).catch(() => {});
            }
          })
          .catch(() => {});

        navigate('/loads');
      } else {
        setLoginError(result.message || t('login.invalidCredentials'));
      }
    } catch (err) {
      setLoginError(t('login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.content}>
        <div style={styles.logoRow}>
          <div style={styles.logoBadge}>
            <Logo height={44} />
          </div>
        </div>

        <h1 style={styles.title}>{t('login.title')}</h1>
        <p style={styles.subtitle}>{t('login.subtitle')}</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <label style={styles.label}>{t('login.email')}</label>
          <input
            {...register('email', { required: true })}
            type="text"
            style={styles.input}
          />
          {errors.email && <p style={styles.errorText}>Required</p>}

          <div style={styles.passwordRow}>
            <label style={styles.label}>{t('login.password')}</label>
            <span style={styles.forgotLink}>{t('login.forgot')}</span>
          </div>
          <PasswordInput
            {...register('password', { required: true })}
            style={styles.input}
          />
          {errors.password && <p style={styles.errorText}>Required</p>}

          {loginError && <p style={styles.errorText}>{loginError}</p>}

          <button type="submit" disabled={loading} style={styles.loginButton}>
            {loading ? '...' : t('login.loginButton')}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.65), rgba(15, 23, 42, 0.75)), url('/login-bg.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  content: {
    width: '100%',
    maxWidth: 380,
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 28,
  },
  logoBadge: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '16px 24px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    display: 'inline-block',
  },
  title: {
    fontSize: 26,
    fontWeight: 800,
    color: 'white',
    marginBottom: 6,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: '#dbeafe',
    marginBottom: 28,
    textAlign: 'center',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: 'white',
    marginBottom: 6,
  },
  passwordRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
  },
  forgotLink: {
    fontSize: 12,
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
	display:'none'
  },
  input: {
    width: '100%',
    padding: '13px 14px',
    borderRadius: 10,
    border: 'none',
    backgroundColor: 'white',
    fontSize: 14,
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 4,
  },
  errorText: {
    color: '#fecaca',
    fontSize: 12,
    marginTop: 2,
    marginBottom: 8,
  },
  loginButton: {
    width: '100%',
    backgroundColor: 'white',
    color: '#2563eb',
    fontWeight: 700,
    fontSize: 15,
    padding: '14px 0',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    marginTop: 24,
  },
};

export default Login;